import { TerminalPricingPlanRequestModel, TerminalPricingPlanResponseModel } from '../../shared/models/dynamic-pricing.model';
import { EncryptionUtil } from '../../shared/utils/encryption.util';
import { StorageKeys } from '../../shared/models/storage-keys';
import { LocalStorageService } from './local-storage.service';
import {
	AirtimeNetworkDto,
	DeviceInformation,
	DeviceRegistrationOtpModel,
	DevicesModel,
	GridTerminalModel,
	PaymentValidationRequest,
	PaymentValidationResponse,
	TerminalInsuranceModel,
	WebClientBillPaymentRequest,
} from '../../shared/models/device.model';
import { StringHelper } from '../helpers/strings.helper';
import { AppConfig } from '../../shared/config/app-config';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AuthenticationService } from './authentication.service';
import { AgentContractModel, HardwareTerminal } from '../../shared/models/transactions-model';
import { Subject } from 'rxjs';
import { MobileWebViewService } from './mobile-web-view.service';
import { DelegateType } from '../../shared/models/utility.model';
import { MobileDelegateService } from './mobile-delegate.service';
import { CookieService } from 'ngx-cookie-service';
import { ViewType } from '../../shared/models/loan.model';
import { AssignedTerminal } from '../../shared/models/sc-terminal-management.model';
import { indexDb } from "../../shared/classes/index-db/index-db";
import { DeviceHelperService } from './device-helper.service'; 
import { logger } from '@mpt-fo/common/telemetry';

@Injectable({
	providedIn: 'root',
})
export class DeviceService extends DeviceDetectorService {
	public strayTerminalsCount = new Subject<any>();
	terminalPricingRequest = new Subject<any>();
	private terminalDetails: HardwareTerminal = null;
	private feeWaiverItem = null;
	private subscriptionId: number;
	private currentTerminalContext: string;
	// tslint:disable-next-line:variable-name
	private _terminalSearchModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_terminalSearchModalDestroy$ = this._terminalSearchModalDestroy.asObservable();
	// tslint:disable-next-line:variable-name
	private _feeWaiverSearchModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_feeWaiverSearchModalDestroy$ = this._feeWaiverSearchModalDestroy.asObservable();
	// tslint:disable-next-line:variable-name
	private _inventoryTerminalSearchModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_inventoryTerminalSearchModalDestroy$ = this._inventoryTerminalSearchModalDestroy.asObservable();
	// tslint:disable-next-line:variable-name
	private _requestedTerminalSearchModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_requestedTerminalSearchModalDestroy$ = this._requestedTerminalSearchModalDestroy.asObservable();
	// tslint:disable-next-line:variable-name
	private _terminalModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_terminalModalDestroy$ = this._terminalModalDestroy.asObservable();
	// tslint:disable-next-line:variable-name
	private _terminalRetrieveModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_terminalRetrieveModalDestroy$ = this._terminalRetrieveModalDestroy.asObservable();
	// tslint:disable-next-line:variable-name
	private _terminalManagementTab = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_terminalManagementTab$ = this._terminalManagementTab.asObservable();

	// tslint:disable-next-line:variable-name
	private _terminalPOSBusinessSearchModalDestroy = new Subject<any>();
	// tslint:disable-next-line:variable-name
	_terminalPOSBusinessSearchModalDestroy$ = this._terminalPOSBusinessSearchModalDestroy.asObservable();
	private terminalSearchContent: any;
	private isTerminalSearch = false;
	private isInventoryTerminalSearch = false;
	private isRequestedTerminalSearch = false;
	private isFeeWaiverTerminalSearch = false;
	private isPOSBusinessSearch = false;
	private businessPOSTerminalSearchContent: any;

	constructor(
		private apiClientHelper: ApiClientHelper,
		public config: AppConfig,
		private localStorage: LocalStorageService,
		private encrypt: EncryptionUtil,
		private authService: AuthenticationService,
		private mobileService: MobileWebViewService,
		private delegateService: MobileDelegateService,
		private deviceHelper: DeviceHelperService,
		private encryptionUtil: EncryptionUtil,
		private cookie: CookieService,
		@Inject(PLATFORM_ID) platformId: any
	) {
		super(platformId);
		if (this.mobileService.isMobileWebViewUser()) {
			this.setMobileAppDeviceInformation()
				.catch(err => {
					logger.error('Error setting device information', err);
				});
		}
	}

	async saveDeviceInformation(device: any) {
		if (device) {
			await this.encryptMobileDeviceId(device?.deviceUniqueIdentifier);
			this.storeDeviceInfo(device?.model, device?.manufacturer);
		}
	}

	async encryptMobileDeviceId(deviceId) {
		const encryptedDeviceId = this.encryptionUtil.encryptData(deviceId);
		await this.saveDeviceIdToIndexDb(encryptedDeviceId);
	}

	async saveDeviceIdToIndexDb(deviceUniqueIdentifier) {
		const deviceUniqueId = await indexDb.deviceUniqueId.get(1);
		const createNewDeviceUniqueId = StringHelper.isNullOrEmptyOrUndefined(deviceUniqueId);
		createNewDeviceUniqueId ? await indexDb.deviceUniqueId.add({ id: 1, deviceUniqueIdentifier })
			: await indexDb.deviceUniqueId.update(1, { deviceUniqueIdentifier });
	}

	storeDeviceInfo(browser, machineOS) {
		this.cookie.set('browser', browser);
		this.cookie.set('machineOS', machineOS);
	}

	setTerminalManagementTab(data) {
		this._terminalManagementTab.next(data);
	}

	setCurrentTerminalContext(context) {
		this.currentTerminalContext = context;
	}

	setTerminalSearchContent(content: any) {
		this.terminalSearchContent = content;
		this.setIsTerminalSearch(true);
	}

	setInventoryTerminalSearchContent(content: any) {
		this.terminalSearchContent = content;
		this.setIsInventoryTerminalSearch(true);
	}

	setFeeWaiverTerminalSearchContent(content: any) {
		this.terminalSearchContent = content;
		this.setIsFeeWaiverTerminalSearch(true);
	}

	setRequestedTerminalSearchContent(content: any) {
		this.terminalSearchContent = content;
		this.setIsRequestedTerminalSearch(true);
	}

	getTerminalSearchContent() {
		return this.terminalSearchContent;
	}

	getIsTerminalSearch() {
		return this.isTerminalSearch;
	}

	getIsInventoryTerminalSearch() {
		return this.isInventoryTerminalSearch;
	}

	getIsRequestedTerminalSearch() {
		return this.isRequestedTerminalSearch;
	}

	getIsFeeWaiverTerminalSearch() {
		return this.isFeeWaiverTerminalSearch;
	}

	setIsTerminalSearch(isSearch) {
		this.isTerminalSearch = isSearch;
	}

	setIsInventoryTerminalSearch(isSearch) {
		this.isInventoryTerminalSearch = isSearch;
	}

	setIsRequestedTerminalSearch(isSearch) {
		this.isRequestedTerminalSearch = isSearch;
	}

	setIsFeeWaiverTerminalSearch(isSearch) {
		this.isFeeWaiverTerminalSearch = isSearch;
	}

	getCurrentTerminalContext() {
		return this.currentTerminalContext;
	}

	destroyTerminalModal() {
		this._terminalModalDestroy.next(true);
	}

	destroySearchTerminalModal(filter) {
		this._terminalSearchModalDestroy.next(filter);
	}

	destroyFeeWaiverSearchModal() {
		this._feeWaiverSearchModalDestroy.next(true);
	}

	destroySearchInventoryTerminalModal(filter) {
		this._inventoryTerminalSearchModalDestroy.next(filter);
	}

	destroySearchRequestedTerminalModal() {
		this._requestedTerminalSearchModalDestroy.next(true);
	}

	destroyTerminalRetreiveModal() {
		this._terminalRetrieveModalDestroy.next(true);
	}

	getDeviceObj(): DeviceInformation {
		const deviceInformation = this.deviceHelper.getDeviceInformation();
		return {
			deviceUniqueIdentifier: deviceInformation?.deviceUniqueIdentifier,
			manufacturer: this.localStorage.get(StorageKeys.machine_os),
			model: this.localStorage.get(StorageKeys.browser),
		};
	}

	setTerminalDetails(terminalDetails: HardwareTerminal) {
		this.terminalDetails = terminalDetails;
	}

	setFeeWaiverItem(feeWaiver) {
		this.feeWaiverItem = feeWaiver;
	}

	setSubscriptionId(id) {
		this.subscriptionId = id;
	}

	getSubscriptionId() {
		return this.subscriptionId;
	}

	getTerminalDetails() {
		return this.terminalDetails;
	}

	getFeeWaiverItem() {
		return this.feeWaiverItem;
	}

	getDevices(params): Promise<DevicesModel> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/user-devices?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalPricingPlanRequests(params: TerminalPricingPlanRequestModel): Promise<TerminalPricingPlanResponseModel> {
		const urlParams = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/merchant-pricing-requests?${urlParams}`;
		return this.apiClientHelper.getForPromise(url);
	}

	treatMerchantPricingPlanRequest(requestId, actionType, body?): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/merchant-pricing-requests/${actionType}/${requestId}`;
		return this.apiClientHelper.putForPromise(url, body);
	}

	getTerminals(params): Promise<AgentContractModel> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/terminals?keyword=&${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAggregatorTerminals(urlRequest, params): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${urlRequest}/get-agents-terminals?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAggregatorAssignedTerminals(context, params): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/assigned-terminals?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getSCAggregatorTerminals(context, params): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/get-agents-terminals?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalDetail(context, terminalId): Promise<AssignedTerminal> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/assigned-terminals/${terminalId}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAggregatorTerminalsByAgentId(urlRequest, id): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${urlRequest}/get-agents-terminals/${id}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getGridTerminals(urlRequest?, params?): Promise<any> {
		// params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/assigned-serial-nos`;
		return this.apiClientHelper.getForPromise(url);
	}

	getInventoryTerminals(urlRequest?, params?): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v2/${urlRequest}/terminal-inventory?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getInventoryTerminalsV3(urlRequest?, params?): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${urlRequest}/terminal-inventory?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getInventoryTerminalDetailV3(urlRequest?, terminalId?): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${urlRequest}/terminals/${terminalId}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAssignedTerminalDetail(urlRequest?, terminalId?): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${urlRequest}/assigned-terminals/${terminalId}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getRequestedTerminals(context?, params?): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/pos-requests/${context}?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessOwnerInfoByPosterminal(params?): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/pos-requests/${params}/aggregator/business`;
		return this.apiClientHelper.getForPromise(url);
	}

	declinePosterminalRequest(params?): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/pos-requests/${params}/aggregator/decline`;
		return this.apiClientHelper.postForPromise(url, '');
	}

	cancelPosterminalRequest(params?): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/pos-requests/${params}/aggregator/cancel`;
		return this.apiClientHelper.postForPromise(url, '');
	}

	setDynamicPricing(params, context): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/${context}/toggle-merchant-pricing`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	setBiometrics(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/user-device/register-biometric`;
		return this.apiClientHelper.postForPromise(url, params, false);
	}

	updateNotificationToken(params): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/user-devices/update-device`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	getAirtimeNetwork(): Promise<AirtimeNetworkDto[]> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/airtime-networks`;
		return this.apiClientHelper.getForPromise(url);
	}

	validateAccountDetails(params: any) {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/validations/account?${params}&cacheAccountDetails=true`;
		return this.apiClientHelper.getForPromise(url);
	}

	getConfig(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/configurations`;
		return this.apiClientHelper.getForPromise(url);
	}

	deactivateDevice(params) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/user-devices/update-device-state`;
		return this.apiClientHelper.putForPromise(url, params);
	}

	activateDevice(params) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/user-device/activate/agent-manager`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	registerDeviceWithOtp(params, role?): Promise<DeviceRegistrationOtpModel> {
		const userRole = this.authService.getContextUser();
		const loginParameter = this.mobileService.getMoniepointAppParameters();
		const clientType = this.mobileService.isMobileWebViewUser()
			? loginParameter?.deviceRegistrationRequest?.model?.toUpperCase()
			: ViewType.WEB;
		let defaultDeviceUrl = `/api/v1/auth/user-device/device-profile/agent-manager`;
		if (userRole && userRole === 'agents') {
			defaultDeviceUrl = `/api/v2/auth/user-device/device-profile`;
		}
		if (this.mobileService.isMobileWebViewUser()) {
			defaultDeviceUrl = '/api/v1/auth/user-device/mobile/device-profile';
		}
		const url = this.config.MONIEPOINT_HOST + defaultDeviceUrl;
		return this.apiClientHelper.postForPromise(url, params, true, true, true, clientType);
	}

	getOtp(params: string, link: string) {
		const url = this.config.MONIEPOINT_HOST + link;
		return this.apiClientHelper.postForPromise(url, params);
	}

	checkIfDeviceIsRegistered(params, username): Promise<any> {
		params = StringHelper.toUrlString(params);
		const encodeUsername = encodeURI(username);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/user-devices/is-web-registered/uniqueIdentifier/${encodeUsername}?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	checkIfDeviceIsRegisteredPostRequest(body: { username; uniqueIdentifier; }) {
		const loginParameter = this.mobileService.getMoniepointAppParameters();
		const clientType = this.mobileService.isMobileWebViewUser()
			? loginParameter?.deviceRegistrationRequest?.model?.toUpperCase()
			: 'WEB';
		const url = this.config.MONIEPOINT_HOST + `/api/v2/user-devices/has-registered-device`;
		return this.apiClientHelper.postForPromise(url, body, true, true, true, clientType);
	}

	getBillers(code: string) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/bill-payments/categories/${code}/billers`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBillerProduct(code: string) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/bill-payments/biller-products/${code}`;
		return this.apiClientHelper.getForPromise(url);
	}

	payBill(request: WebClientBillPaymentRequest) {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/bill-payment`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	validateBill(request: PaymentValidationRequest): Promise<PaymentValidationResponse> {
		const params = StringHelper.toUrlString(request);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/wallets/bill-payment/validate?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalReasons(request, context): Promise<any> {
		const params = StringHelper.toUrlString(request);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/terminal-retrieval-reasons?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalSwappingReasons(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/terminal-swap/reasons`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalSerialNos(params, terminalContext?): Promise<GridTerminalModel> {
		const context = terminalContext ? terminalContext : `${this.authService.getContextUser()}s`;
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/${context}/assigned-serial-nos?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAvailableTerminalForAssignment(params, terminalContext?): Promise<GridTerminalModel> {
		const context = terminalContext ? terminalContext : `${this.authService.getContextUser()}s`;
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v2/${context}/assigned-serial-nos?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	acceptPosRequestTerminal(params, terminalContext?): Promise<any> {
		const context = terminalContext ? terminalContext : `${this.authService.getContextUser()}s`;
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v2/${context}/assigned-serial-nos?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getStrayTerminal(params): Promise<GridTerminalModel> {
		const context = `${this.authService.getContextUser()}s`;
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/${context}/strayed-serial-nos?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	retrieveTerminal(request, context): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/retrieve-terminal`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	retrieveTerminalV2(request, context): Promise<TerminalRetrievalResponse> {
		const url = this.config?.MONIEPOINT_TERMINAL + `/api/v2/${context}/retrieve-terminal`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	swapTerminal(request, context): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/${context}/swap-terminal`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, request);
	}

	validateSwapTerminalEligibility(request): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/terminal-swap/validations`;
		return this.apiClientHelper.postForPromise(url, request);
	}

	getFaultsReason() {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/terminal-repair-requests/faults`;
		return this.apiClientHelper.getForPromise(url);
	}

	terminalSubscription(request): Promise<any> {
		const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/insure/${request}`;
		return this.apiClientHelper.postForPromise(url, '');
	}

	cancelSubscription(request): Promise<any> {
		const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/${request}`;
		return this.apiClientHelper.delete(url);
	}

	// terminalSubscription(request):Promise<any>{
	//   const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/manual/${request}`;
	//   return this.apiClientHelper.postForPromise(url,'');
	// }

	getAvailableTerminalSubscriptions(): Promise<TerminalInsuranceModel[]> {
		const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/available`;
		return this.apiClientHelper.getForPromise(url);
	}

	purchaseSubscription(): Promise<any> {
		const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/purchase`;
		return this.apiClientHelper.postForPromise(url, '');
	}

	attachSubscriptionToTerminal(params): Promise<any> {
		const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/attach`;
		return this.apiClientHelper.postForPromise(url, params);
	}

	getMySubscriptions(request): Promise<any> {
		request = StringHelper.toUrlString(request);
		const url = this.config.MONIEPOINT_INSURANCE + `/api/v1/insurance/subscription/paged?${request}`;
		return this.apiClientHelper.getForPromise(url);
	}

	terminalSwap(context: string, request): Promise<any> {
		const payload = request;
		if (context === 'super-aggregators') {
			const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/swap-terminal`;
			return this.apiClientHelper.newPostAsFormDataForPromise(url, payload);
		} else {
			delete payload?.globalSwapEnabled;
			return this.swapTerminalV2(payload);
		}
	}

	getSerialNosByTerminalType(context: string, terminalType: string): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/assigned-serial-nos?type=${terminalType}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAssignableSerialNosByTerminalType(context: string, terminalType: string, swapOutTerminalId: number, serial?: string): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/${context}/swappable-terminals?terminalType=${terminalType}&swapOutTerminalId=${swapOutTerminalId}&pageNumber=1&pageSize=1500&serialNumber=${serial ?? ''}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalInfo(): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agents/aggregators/agentTerminalInfo`;
		return this.apiClientHelper.getForPromise(url);
	}

	getMerchantPricingRequests(params): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/aggregators/merchant-pricing-requests?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getUserDeviceByChannel(params) {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_HOST + `/api/v1/user-devices/get-devices-by-channel?${params}`;
		return this.apiClientHelper.getForPromise(url, true);
	}

	terminalRetrievalRequestStatus(terminalId: number): Promise<any> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/terminals/${terminalId}/retrieval/request-status`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalSwapReasons(swapInTerminalType: string, swapOutTerminalType: string, swapInOwnershipState = '', swapOutOwnershipState = ''): Promise<any> { 
		const url =
			this.config.MONIEPOINT_TERMINAL +
			`/api/v1/terminal-swap/reasons?swapInTerminalType=${swapInTerminalType}&swapOutTerminalType=${swapOutTerminalType}&swapInTerminalOwnershipState=${swapInOwnershipState}&swapOutTerminalOwnershipState=${swapOutOwnershipState}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAllSerialNoByTerminalType(terminalType: string): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/aggregators/assigned-serial-nos?terminalType=${terminalType}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTerminalSwapCharges(swapInTerminalId: number, swapOutTerminalId: number, swapReason?: string): Promise<any> {
		const url =
			this.config.MONIEPOINT_TERMINAL +
			`/api/v1/terminal-swap/charges?swapInTerminalId=${swapInTerminalId}&swapOutTerminalId=${swapOutTerminalId}&swapReason=${swapReason}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getRepairCodeBySerialNo(serialNumber: string): Promise<{repairCode: string}> {
		const url =
			this.config.MONIEPOINT_TERMINAL +
			`/api/v1/terminals/${serialNumber}/repair-code`;
		return this.apiClientHelper.getForPromise(url);
	}

	swapTerminalV2(request): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v2/aggregators/swap-terminal`;
		return this.apiClientHelper.newPostAsFormDataForPromise(url, request);
	}

	getTerminalAssignmentRequests(context: string, params): Promise<any> {
		params = StringHelper.toUrlString(params);
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/terminal-assignment-processes/${context}?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getProcessTasksByProcessId(processId: number): Promise<any> {
		const url = this.config.MONIEPOINT_TERMINAL + `/api/v1/terminal-assignment-processes/${processId}/tasks`;
		return this.apiClientHelper.getForPromise(url);
	}

	destroyPOSBusinessSearchTerminalModal() {
		this._terminalPOSBusinessSearchModalDestroy.next(true);
	}

	setIsBusinessPOSTerminalSearch(isSearch) {
		this.isPOSBusinessSearch = isSearch;
	}

	getIsPOSBusinessTerminalSearch() {
		return this.isPOSBusinessSearch;
	}

	getPOSBusinessTerminalSearchContent() {
		return this.businessPOSTerminalSearchContent;
	}

	setPOSBusinessTerminalSearchContent(content: any) {
		this.businessPOSTerminalSearchContent = content;
		this.setIsBusinessPOSTerminalSearch(true);
	}

	private async setMobileAppDeviceInformation() {
		const isFlutterInAppWebViewPlatformReady = await this.mobileService.isFlutterInAppWebViewPlatformReady();
		isFlutterInAppWebViewPlatformReady ? await this.setMobileDeviceInformation()
			: await this.handleNoFlutterInAppWebViewPlatformReadyEvent();
	}

	async handleNoFlutterInAppWebViewPlatformReadyEvent() {
		await this.addFlutterInAppWebViewPlatformReadyEventListener();
		await this.setMobileDeviceInformation();
	}

	async addFlutterInAppWebViewPlatformReadyEventListener() {
		window.addEventListener('flutterInAppWebViewPlatformReady', async () => {
			await this.mobileService.setFlutterInAppWebViewPlatformReady(true);
		});
	}

	private async setMobileDeviceInformation() {
		const res = await this.delegateService.getDelegateData(DelegateType.getDeviceInformation);
		const deviceInformation = res ? JSON.parse(res) : null;
		await this.saveDeviceInformation(deviceInformation);
	}
}

export enum TerminalType {
	MPOS = 'MPOS',
	SMART = 'SMART',
}

export const TERMINAL_STATUS = {
	GOOD: 'GOOD',
	FAULTY: 'FAULTY',
} as const;

export interface RetrievalReasonResponse {
	id?: number;
	terminalRetrievalReason?: string;
	terminalRetrievalState?: string;
}

export interface TerminalDetailResponse {
	hardwareTerminalId?: number;
	serialNumber?: string;
	insuranceStatus?: string;
	businessName?: string;
	terminalId?: string;
	activationStatus?: string;
	manufacturer?: string;
	hasPendingRetrievalRequest?: boolean;
	terminalModel?: TerminalModelResponse;
	eligibleForSwap?: boolean;
}

export interface TerminalModelResponse {
	terminalType?: string;
	manufacturer?: string;
	operatingSystem?: string;
	configuration?: TerminalConfiguration;
}

export interface TerminalConfiguration {
	swappable?: boolean;
	retrievable?: boolean;
	weeklyPerformanceTarget?: number;
}

export interface TerminalRetrievalResponse {
	statusCode?: string,
	validated?: boolean,
	hasUnsyncedTransactions?: boolean,
	retrievalWorkflowInitiated?: boolean,
	businessOwner?: CustodianDetail,
	scCustodian?: CustodianDetail;
	responseMessage?: string;
}

export interface CustodianDetail {
	id?: number;
	businessName?: string;
	businessPhone?: string;
	businessEmail?: string;
	imageReference?: string;
}
